import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ReferenceArea,
} from "recharts";
import { Card, Kpi, SectionTitle } from "../ui";
import { FilterChip, ActivityTable, axisProps, tipProps } from "../charts";
import { day, pad2, weekStartOf } from "@/lib/dash";
import { Activity, DashboardData } from "@/lib/types";
import { fmtNum, fmtDate, fmtDMY } from "@/lib/format";
import { useUI } from "@/lib/theme";

export default function Carga({
  load,
  acts,
  gearMap,
}: {
  load: DashboardData["load"];
  acts: Activity[];
  gearMap: Record<string, string>;
}) {
  const { t, loc, palette } = useUI();
  const RED = "#ef4444";
  // Selección como rango de fechas [from, to] (ISO inclusive). Clic = semana; arrastre = rango.
  const [selection, setSelection] = useState<{ from: string; to: string } | null>(null);
  const [drag, setDrag] = useState<{ start: string; cur: string } | null>(null);
  useEffect(() => setSelection(null), [load]);

  const addDaysIso = (iso: string, n: number) => {
    const d = day(iso);
    d.setDate(d.getDate() + n);
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  };

  // Actividades del rango seleccionado (inclusivo).
  const selActs = useMemo(() => {
    if (!selection) return [];
    const start = day(selection.from);
    const end = day(selection.to);
    end.setDate(end.getDate() + 1); // exclusivo
    return acts
      .filter((a) => {
        const d = new Date(a.start_local);
        return d >= start && d < end;
      })
      .sort((a, b) => b.start_local.localeCompare(a.start_local));
  }, [acts, selection]);

  // Banda de resaltado: solo en vivo mientras arrastras (preview). Una vez fijada la
  // selección, los gráficos se recortan a ese rango (zoom), así que la banda sobra.
  const band = useMemo(() => {
    if (!load.length || !drag) return null;
    let [x1, x2] = drag.start <= drag.cur ? [drag.start, drag.cur] : [drag.cur, drag.start];
    const first = load[0].date,
      last = load[load.length - 1].date;
    if (x1 < first) x1 = first;
    if (x2 > last) x2 = last;
    return { x1, x2 };
  }, [drag, load]);

  // Datos visibles en los gráficos: si hay selección fijada, se recortan a ese rango
  // (efecto zoom); si no, se ve la serie completa.
  const chartData = useMemo(
    () =>
      selection
        ? load.filter((l) => l.date >= selection.from && l.date <= selection.to)
        : load,
    [load, selection],
  );

  // Ratón: mousedown inicia, mousemove arrastra, mouseup/leave finaliza.
  const onDown = (st: any) => {
    const d = st?.activeLabel;
    if (d) setDrag({ start: d, cur: d });
  };
  const onMove = (st: any) => {
    const d = st?.activeLabel;
    if (d) setDrag((c) => (c ? { ...c, cur: d } : c));
  };
  const finalize = () => {
    if (drag) {
      const diff = Math.abs((day(drag.cur).getTime() - day(drag.start).getTime()) / 86400000);
      if (diff < 3) {
        // Clic (sin arrastre real): selecciona/deselecciona la semana de ese punto.
        const wk = weekStartOf(drag.start);
        const wkSel = { from: wk, to: addDaysIso(wk, 6) };
        setSelection((cur) => (cur && cur.from === wkSel.from && cur.to === wkSel.to ? null : wkSel));
      } else {
        const [a, b] = drag.start <= drag.cur ? [drag.start, drag.cur] : [drag.cur, drag.start];
        setSelection({ from: a, to: b });
      }
    }
    setDrag(null);
  };

  const selLabel = selection
    ? selection.from === selection.to
      ? fmtDate(selection.from, loc)
      : `${fmtDate(selection.from, loc)} – ${fmtDate(selection.to, loc)}`
    : "";
  const mouseEvents = {
    onMouseDown: onDown,
    onMouseMove: onMove,
    onMouseUp: finalize,
    onMouseLeave: finalize,
  };

  if (load.length === 0) return <Card>{t("txt_sin_carga")}</Card>;

  // Cuando hay selección interna (click/drag en el gráfico), los KPIs reflejan ESE rango.
  const kpiLoad = selection
    ? load.filter((l) => l.date >= selection.from && l.date <= selection.to)
    : load;
  const ult = kpiLoad.length ? kpiLoad[kpiLoad.length - 1] : load[load.length - 1];
  const first = kpiLoad.length ? kpiLoad[0] : load[0];
  const periodDays = Math.round((day(ult.date).getTime() - day(first.date).getTime()) / 86400000);
  const arrowDelta = (v: number) => `${v >= 0 ? "↑" : "↓"} ${fmtNum(Math.abs(v), 0, loc)}`;
  const deltaSub = (v: number) => `${arrowDelta(v)} ${t("kpi_over_days", { d: periodDays })}`;
  const tsbState = ult.tsb < -10 ? t("tsb_fatiga") : ult.tsb > 5 ? t("tsb_fresco") : t("tsb_equilibrio");
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        {selection && (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-accent/40 bg-accent/10 px-3 py-1 text-xs font-semibold text-accent">
            {selLabel}
            <button onClick={() => setSelection(null)} className="ml-1 opacity-60 hover:opacity-100" aria-label="Quitar filtro">✕</button>
          </span>
        )}
      </div>
      <div className="grid grid-cols-3 gap-4">
        <Kpi
          label={t("kpi_fitness")}
          value={fmtNum(ult.ctl, 0, loc)}
          accent
          info={t("info_ctl")}
          sub={periodDays > 0 ? deltaSub(ult.ctl - first.ctl) : undefined}
        />
        <Kpi
          label={t("kpi_fatiga")}
          value={fmtNum(ult.atl, 0, loc)}
          accent
          info={t("info_atl")}
          sub={periodDays > 0 ? deltaSub(ult.atl - first.atl) : undefined}
        />
        <Kpi
          label={t("kpi_forma")}
          value={fmtNum(ult.tsb, 0, loc)}
          accent
          info={t("info_tsb")}
          sub={periodDays > 0 ? `${tsbState} · ${arrowDelta(ult.tsb - first.tsb)}` : tsbState}
        />
      </div>

      <Card>
        <div className="flex items-center justify-between">
          <SectionTitle info={`${t("info_ctl")} ${t("info_atl")}`}>{t("sec_ctl_atl")}</SectionTitle>
          <span className="hidden text-xs text-muted sm:block">{t("hint_carga")}</span>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData} className="cursor-crosshair select-none" {...mouseEvents}>
            <CartesianGrid stroke={palette.grid} strokeOpacity={0.5} vertical={false} />
            <XAxis
              dataKey="date"
              {...axisProps(palette)}
              minTickGap={60}
              tickFormatter={(v) => fmtDMY(v, loc)}
            />
            <YAxis {...axisProps(palette)} />
            <Tooltip {...tipProps(palette)} labelFormatter={(v) => fmtDate(String(v), loc)} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {band && (
              <ReferenceArea
                x1={band.x1}
                x2={band.x2}
                fill={RED}
                fillOpacity={0.12}
                stroke={RED}
                strokeOpacity={0.3}
              />
            )}
            <Line
              type="monotone"
              dataKey="atl"
              name={t("legend_fatiga")}
              stroke="#ef4444"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="ctl"
              name={t("legend_fitness")}
              stroke="#3b82f6"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      <Card>
        <SectionTitle info={t("info_tsb")}>{t("sec_tsb")}</SectionTitle>
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={chartData} className="cursor-crosshair select-none" {...mouseEvents}>
            <defs>
              <linearGradient id="tsb" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#ef4444" stopOpacity={0.5} />
                <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke={palette.grid} strokeOpacity={0.5} vertical={false} />
            <XAxis
              dataKey="date"
              {...axisProps(palette)}
              minTickGap={60}
              tickFormatter={(v) => fmtDMY(v, loc)}
            />
            <YAxis {...axisProps(palette)} />
            <Tooltip {...tipProps(palette)} labelFormatter={(v) => fmtDate(String(v), loc)} />
            {band && (
              <ReferenceArea
                x1={band.x1}
                x2={band.x2}
                fill={RED}
                fillOpacity={0.12}
                stroke={RED}
                strokeOpacity={0.3}
              />
            )}
            <Area
              type="monotone"
              dataKey="tsb"
              name={t("legend_forma")}
              stroke="#ef4444"
              fill="url(#tsb)"
              strokeWidth={2}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
        <p className="mt-2 text-xs text-muted">{t("ref_tsb")}</p>
      </Card>

      {selection && (
        <Card>
          <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
            <SectionTitle>{t("sec_acts_periodo")}</SectionTitle>
            <div className="flex flex-wrap items-center gap-2">
              <FilterChip label={selLabel} onClear={() => setSelection(null)} color={RED} />
            </div>
          </div>
          <ActivityTable acts={selActs} gearMap={gearMap} />
        </Card>
      )}
    </div>
  );
}
